import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';

// Tactical Command Center Animations
const hudRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const scanSweep = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const nodePulse = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
`;

const dataFlow = keyframes`
  from { strokeDashoffset: 100; }
  to { strokeDashoffset: 0; }
`;

const loadingPulse = keyframes`
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
`;

const barFluctuate = keyframes`
  0% { height: 60%; }
  50% { height: 85%; }
  100% { height: 60%; }
`;

const progressFill = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

/**
 * Orbital Rings Spinner - Tactical Command Center
 * Dark Mode War Room Style
 */
export const OrbitalRingsSpinner = ({ size = 200, message = '' }) => {
  const centerSize = size * 0.6;
  const panelWidth = size * 0.2;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: size * 2.5,
        height: size * 1.4,
        position: 'relative',
        background: 'radial-gradient(circle at center, #0a0a1a 0%, #000000 70%)',
      }}
    >
      {/* Scan Lines Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 191, 255, 0.05) 2px, rgba(0, 191, 255, 0.05) 4px)',
          pointerEvents: 'none',
        }}
      />

      {/* Grid Overlay */}
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0.08,
          pointerEvents: 'none',
        }}
      >
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#00BFFF" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      {/* LEFT PANEL */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: panelWidth,
          height: size,
          background: 'rgba(10, 10, 10, 0.85)',
          border: '1px solid #00BFFF',
          boxShadow: '0 0 10px rgba(0, 191, 255, 0.6)',
          padding: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Progress Indicator */}
        <Box sx={{ borderBottom: '1px solid rgba(0, 191, 255, 0.3)', pb: 0.5 }}>
          <Box sx={{ color: '#00BFFF', fontSize: '14px', fontWeight: 700, fontFamily: 'monospace' }}>
            LOAD
          </Box>
          <Box sx={{ color: '#00FFFF', fontSize: '16px', fontWeight: 700, fontFamily: 'monospace' }}>
            100%
          </Box>
          <Box sx={{ height: 3, background: 'rgba(0, 191, 255, 0.2)', borderRadius: 1, mt: 0.5 }}>
            <Box sx={{ height: '100%', width: '75%', background: '#00BFFF', borderRadius: 1, boxShadow: '0 0 5px #00BFFF' }} />
          </Box>
        </Box>

        {/* Vertical Bar Chart */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 0.3, px: 0.5 }}>
          {[65, 85, 70, 90, 75, 80, 95, 70].map((height, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: `${height}%`,
                background: 'linear-gradient(to top, #00BFFF, #00FFFF)',
                opacity: 0.7,
                boxShadow: '0 0 3px #00BFFF',
                animation: `${barFluctuate} ${2 + i * 0.2}s ease-in-out infinite`,
              }}
            />
          ))}
        </Box>

        {/* Status Text */}
        <Box sx={{ fontSize: '6px', color: '#00BFFF', fontFamily: 'monospace', opacity: 0.6, lineHeight: 1.2 }}>
          SCANNING TRADE COMPLIANCE DATA... ANALYZING RISK PATTERNS... MONITORING GLOBAL TRANSACTIONS...
        </Box>
      </Box>

      {/* CENTER HUD */}
      <Box
        sx={{
          position: 'relative',
          width: centerSize,
          height: centerSize,
          zIndex: 2,
        }}
      >
        <svg width={centerSize} height={centerSize} viewBox="0 0 200 200">
          <defs>
            {/* Glow Filter */}
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Concentric Rings */}
          {[80, 65, 50, 35].map((r, i) => (
            <circle
              key={i}
              cx="100"
              cy="100"
              r={r}
              fill="none"
              stroke="#00BFFF"
              strokeWidth="1"
              opacity={0.2 + i * 0.2}
              filter="url(#glow)"
            />
          ))}

          {/* Rotating HUD Ring */}
          <g style={{ transformOrigin: 'center', animation: `${hudRotate} 20s linear infinite` }}>
            <circle
              cx="100"
              cy="100"
              r="90"
              fill="none"
              stroke="#00BFFF"
              strokeWidth="1.5"
              strokeDasharray="10,5"
              opacity="0.4"
            />
            {/* Radial Nodes */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
              const rad = (angle * Math.PI) / 180;
              const x = 100 + 90 * Math.cos(rad);
              const y = 100 + 90 * Math.sin(rad);
              return (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r="3"
                  fill="#00FFFF"
                  filter="url(#glow)"
                  style={{ animation: `${nodePulse} ${2 + i * 0.1}s ease-in-out infinite` }}
                />
              );
            })}
          </g>

          {/* Scan Sweep Line */}
          <g style={{ transformOrigin: 'center', animation: `${scanSweep} 3s linear infinite` }}>
            <line
              x1="100"
              y1="100"
              x2="100"
              y2="10"
              stroke="url(#sweepGradient)"
              strokeWidth="2"
            />
            <defs>
              <linearGradient id="sweepGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#00FFFF" stopOpacity="0"/>
                <stop offset="100%" stopColor="#00FFFF" stopOpacity="1"/>
              </linearGradient>
            </defs>
          </g>

          {/* Center Triangle Icon */}
          <path
            d="M 100 80 L 115 105 L 85 105 Z"
            fill="none"
            stroke="#00FFFF"
            strokeWidth="2"
            filter="url(#glow)"
          />

          {/* Technical Text Paths */}
          <g opacity="0.5">
            <text x="100" y="25" textAnchor="middle" fill="#00BFFF" fontSize="8" fontFamily="monospace">
              THREAT ASSESSMENT
            </text>
            <text x="100" y="175" textAnchor="middle" fill="#00BFFF" fontSize="8" fontFamily="monospace">
              COMMAND CENTER
            </text>
          </g>
        </svg>
      </Box>

      {/* RIGHT PANEL */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: panelWidth,
          height: size,
          background: 'rgba(10, 10, 10, 0.85)',
          border: '1px solid #00BFFF',
          boxShadow: '0 0 10px rgba(0, 191, 255, 0.6)',
          padding: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Metric Display */}
        <Box sx={{ borderBottom: '1px solid rgba(0, 191, 255, 0.3)', pb: 0.5 }}>
          <Box sx={{ color: '#00BFFF', fontSize: '14px', fontWeight: 700, fontFamily: 'monospace' }}>
            NSUS
          </Box>
          <Box sx={{ color: '#00FFFF', fontSize: '16px', fontWeight: 700, fontFamily: 'monospace' }}>
            98%
          </Box>
          <Box sx={{ width: 30, height: 30, border: '2px solid #00BFFF', borderRadius: '50%', mt: 0.5, position: 'relative' }}>
            <Box sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'conic-gradient(#00BFFF 0deg, #00BFFF 28.8deg, transparent 28.8deg)',
            }} />
          </Box>
        </Box>

        {/* Horizontal Sliders */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[75, 60, 85, 70, 90].map((width, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, border: '1px solid #00BFFF', borderRadius: '50%' }} />
              <Box sx={{ flex: 1, height: 3, background: 'rgba(0, 191, 255, 0.2)', borderRadius: 1 }}>
                <Box sx={{ height: '100%', width: `${width}%`, background: '#00BFFF', borderRadius: 1, boxShadow: '0 0 3px #00BFFF' }} />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Vertical Histogram */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 0.2, px: 0.5 }}>
          {[70, 85, 65, 90, 75].map((height, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: `${height}%`,
                background: '#00BFFF',
                opacity: 0.6,
                boxShadow: '0 0 3px #00BFFF',
              }}
            />
          ))}
        </Box>

        {/* Status Text */}
        <Box sx={{ fontSize: '6px', color: '#00BFFF', fontFamily: 'monospace', opacity: 0.6, lineHeight: 1.2 }}>
          REAL-TIME COMPLIANCE MONITORING ACTIVE... PROCESSING DATA STREAMS...
        </Box>
      </Box>

      {/* BOTTOM CENTER - Loading Text */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            color: '#00BFFF',
            fontSize: '24px',
            fontWeight: 700,
            letterSpacing: '4px',
            fontFamily: 'Orbitron, monospace',
            textTransform: 'uppercase',
            animation: `${loadingPulse} 2s ease-in-out infinite`,
            textShadow: '0 0 10px #00BFFF',
          }}
        >
          Loading
        </Box>
        <Box
          sx={{
            width: 500,
            height: 3,
            background: 'rgba(0, 191, 255, 0.2)',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 'inset 0 0 5px rgba(0, 191, 255, 0.3)',
          }}
        >
          <Box
            sx={{
              height: '100%',
              background: 'linear-gradient(90deg, #00BFFF, #00FFFF)',
              boxShadow: '0 0 10px #00BFFF',
              animation: `${progressFill} 4s ease-in-out infinite`,
            }}
          />
        </Box>
      </Box>

      {/* Message */}
      {message && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            color: '#00BFFF',
            fontSize: '12px',
            fontFamily: 'monospace',
            opacity: 0.7,
          }}
        >
          {message}
        </Box>
      )}
    </Box>
  );
};

export default OrbitalRingsSpinner;
